import React, { useEffect, useState } from "react";
import { Download, Bell, BellOff, Check, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  temSuporte, permissaoAtual, jaInscrito, inscrever, cancelar, enviarTeste,
} from "../lib/pwa";

// Dois pedidos num cartão só, porque na cabeça do advogado é uma coisa:
// "ter o TUTO no celular e ser avisado". Separar em duas telas faria ele
// instalar o app e nunca ligar o aviso, que é o que dá utilidade ao app.

const JA_DISPENSOU = "tuto:instalar-dispensado";

export default function InstalarApp() {
  const [evento, setEvento] = useState(null);
  const [instalado, setInstalado] = useState(false);
  const [inscrito, setInscrito] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [dispensado, setDispensado] = useState(() => {
    try {
      return localStorage.getItem(JA_DISPENSOU) === "1";
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    // O Chrome avisa quando a instalação é possível e guarda o direito de
    // mostrar o diálogo — por isso o evento precisa ser segurado até o clique.
    const capturar = (e) => {
      e.preventDefault();
      setEvento(e);
    };
    window.addEventListener("beforeinstallprompt", capturar);

    const jaRodando = window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    setInstalado(Boolean(jaRodando));

    jaInscrito().then(setInscrito).catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", capturar);
  }, []);

  const instalar = async () => {
    if (!evento) return;
    evento.prompt();
    const escolha = await evento.userChoice;
    setEvento(null);
    if (escolha?.outcome === "accepted") setInstalado(true);
  };

  const ligarAvisos = async () => {
    setOcupado(true);
    try {
      await inscrever();
      setInscrito(true);
      await enviarTeste();
      toast.success("Avisos ligados. Mandamos um de teste para este aparelho.");
    } catch (e) {
      toast.error(e?.message || "Não foi possível ligar os avisos.");
    } finally {
      setOcupado(false);
    }
  };

  const desligarAvisos = async () => {
    setOcupado(true);
    try {
      await cancelar();
      setInscrito(false);
      toast.success("Avisos desligados neste aparelho.");
    } finally {
      setOcupado(false);
    }
  };

  const podeInstalar = Boolean(evento) && !instalado;
  const podeAvisar = temSuporte() && permissaoAtual() !== "unsupported";

  // Nada a oferecer: app já instalado e avisos já ligados, ou navegador sem
  // suporte a nenhum dos dois.
  if ((!podeInstalar && !podeAvisar) || (instalado && inscrito) || dispensado) return null;

  const dispensar = () => {
    setDispensado(true);
    try {
      localStorage.setItem(JA_DISPENSOU, "1");
    } catch (e) {
      // Modo privativo: some nesta sessão e volta na próxima. Aceitável.
    }
  };

  return (
    <div
      data-testid="instalar-app"
      className="bg-[#F4F7FB] border border-[#D1DEEE] rounded-lg p-5 flex flex-wrap items-center gap-4"
    >
      <div className="bg-[#003882] text-white rounded-lg p-2.5 shrink-0">
        <Smartphone size={20} />
      </div>
      <div className="flex-1 min-w-[220px]">
        <h3 className="font-semibold text-[#001533]">
          {instalado ? "Ligue os avisos no celular" : "Leve o TUTO para o celular"}
        </h3>
        <p className="text-sm text-[#335480] mt-0.5">
          {instalado
            ? "Intimação nova, movimentação nos seus processos e mensagem nossa chegam como notificação."
            : "Instale o app e receba intimações e movimentações como notificação, sem precisar abrir."}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {podeInstalar && (
          <Button
            data-testid="btn-instalar"
            onClick={instalar}
            className="bg-[#003882] hover:bg-[#00285e] text-white"
          >
            <Download size={16} /> Instalar
          </Button>
        )}
        {podeAvisar && !inscrito && (
          <Button
            data-testid="btn-ligar-avisos"
            onClick={ligarAvisos}
            disabled={ocupado}
            variant={podeInstalar ? "outline" : "default"}
            className={podeInstalar
              ? "border-[#D1DEEE] text-[#335480]"
              : "bg-[#003882] hover:bg-[#00285e] text-white"}
          >
            <Bell size={16} /> {ocupado ? "Ligando..." : "Ligar avisos"}
          </Button>
        )}
        {podeAvisar && inscrito && (
          <Button
            data-testid="btn-desligar-avisos"
            onClick={desligarAvisos}
            disabled={ocupado}
            variant="outline"
            className="border-[#D1DEEE] text-[#335480]"
          >
            <BellOff size={16} /> Desligar avisos
          </Button>
        )}
        <button
          onClick={dispensar}
          className="text-sm text-[#6685AD] hover:text-[#335480] px-2"
          title="Não mostrar de novo"
        >
          Agora não
        </button>
      </div>
      {instalado && inscrito && (
        <span className="flex items-center gap-1 text-sm text-green-700">
          <Check size={16} /> Tudo pronto
        </span>
      )}
    </div>
  );
}
